// src/features/auth/AuthProvider.tsx
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setTokens, clearAuthTokens } from './authTokensSlice'
import { useCheckIdTokenQuery, useExchangeCodeMutation, useLogoutMutation } from './idApi'
import { useObtainTokenMutation } from './authApi'
import { api } from '@/store/api'
import { idApi } from '@/store/idApi'

interface AuthContextValue {
  isAuthorized: boolean
  isAuthorizeLoading: boolean
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// If the backend keeps rejecting freshly obtained tokens, every 401 clears
// tokens and re-triggers SSO recovery (get-jwt → obtain → 401 → …) — an
// infinite request loop. Cap the attempts per session to break the cycle.
const MAX_SSO_RECOVERY_ATTEMPTS = 3

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch()
  const accessToken = useAppSelector((s) => s.authTokens.accessToken)
  const [isAuthorized, setIsAuthorized] = useState<boolean>(!!accessToken)
  const [isAuthorizeLoading, setIsAuthorizeLoading] = useState<boolean>(true)

  const [obtainToken] = useObtainTokenMutation()
  const [exchangeCode] = useExchangeCodeMutation()
  const [logoutReq] = useLogoutMutation()
  const {
    data: idTokenData,
    isLoading: idTokenLoading,
    isError: idTokenError,
  } = useCheckIdTokenQuery(undefined, { skip: !!accessToken })

  // Set synchronously while reading the URL so the second effect skips its
  // tryIdServiceLogin branch even on its first render — get-jwt resolves in
  // parallel and would otherwise race us to clearAuthTokens.
  const ssoAttemptsRef = useRef(0)

  const authCodeInProgress = useRef<boolean>(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('authCode'),
  )

  // Effect 1: magic-link / OAuth callback. Runs once with empty deps so a
  // parallel get-jwt rejection cannot cancel us mid-flight (was the Safari bug).
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const authCode = urlParams.get('authCode')
    if (!authCode) return

    // Strip callback params from URL before any await so a remount or
    // re-render cannot re-trigger exchange-code with the same one-time code.
    urlParams.delete('authCode')
    urlParams.delete('oauthLoginResult')
    urlParams.delete('isRegistered')
    const newSearch = urlParams.toString()
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`,
    )

    const exchangeAndObtain = async () => {
      try {
        const exchange = await exchangeCode({ code: authCode }).unwrap()
        const tokens = await obtainToken(exchange.token).unwrap()
        dispatch(setTokens(tokens))
        setIsAuthorized(true)
      } catch {
        setIsAuthorized(false)
      } finally {
        authCodeInProgress.current = false
        setIsAuthorizeLoading(false)
      }
    }
    void exchangeAndObtain()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount; deps would race with parallel get-jwt
  }, [])

  // Effect 2: regular auth recovery — persisted token or get-jwt fallback.
  useEffect(() => {
    if (authCodeInProgress.current) return

    let cancelled = false

    const run = async () => {
      // Token already in Redux (rehydrated from localStorage)
      if (accessToken) {
        setIsAuthorized(true)
        setIsAuthorizeLoading(false)
        return
      }

      if (idTokenLoading) return

      if (idTokenError || !idTokenData?.token) {
        setIsAuthorized(false)
        setIsAuthorizeLoading(false)
        return
      }

      if (ssoAttemptsRef.current >= MAX_SSO_RECOVERY_ATTEMPTS) {
        setIsAuthorized(false)
        setIsAuthorizeLoading(false)
        return
      }
      ssoAttemptsRef.current += 1

      try {
        const tokens = await obtainToken(idTokenData.token).unwrap()
        if (cancelled) return
        dispatch(setTokens(tokens))
        setIsAuthorized(true)
      } catch {
        if (cancelled) return
        setIsAuthorized(false)
      } finally {
        if (!cancelled) setIsAuthorizeLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [accessToken, obtainToken, dispatch, idTokenData, idTokenLoading, idTokenError])

  const logout = async () => {
    try {
      await logoutReq().unwrap()
    } catch {
      // proceed with local cleanup regardless
    }
    dispatch(clearAuthTokens())
    dispatch(api.util.resetApiState())
    dispatch(idApi.util.resetApiState())
    setIsAuthorized(false)
  }

  return (
    <AuthContext.Provider value={{ isAuthorized, isAuthorizeLoading, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
